import type { AnimalSummary } from '@/api/hooks';
import type { Animal } from '@/types/api';

export function animalToPatchBody(animal: Animal) {
  return {
    property_id: animal.property_id,
    internal_number: animal.internal_number,
    name: animal.name,
    sex: animal.sex,
    lot_id: animal.lot_id,
    category_code: animal.category_code,
    breed_id: animal.breed_id,
    birth_date: animal.birth_date,
    weaning_date: animal.weaning_date,
    current_weight_kg: animal.current_weight_kg,
    chip_number: animal.chip_number,
    registry_code: animal.registry_code,
    notes: animal.notes,
    is_archive: animal.is_archive ?? false,
    mother_id: animal.mother_id,
    father_id: animal.father_id,
  };
}

export type PedigreeSlotKey =
  | 'maternal_grandmother'
  | 'maternal_grandfather'
  | 'paternal_grandmother'
  | 'paternal_grandfather'
  | 'mother'
  | 'father';

export type PedigreeSlot = {
  key: PedigreeSlotKey;
  label: string;
  shortLabel: string;
  requiredSex: 'female' | 'male';
  /** Animal cujo registro será atualizado (pai/mãe do slot). */
  targetAnimalId: number;
  field: 'mother_id' | 'father_id';
  value: number | null;
  disabled: boolean;
  disabledReason?: string;
};

export function buildPedigreeSlots(animal: Animal, animalsById: Map<number, Animal>): PedigreeSlot[] {
  const mother = animal.mother_id ? animalsById.get(animal.mother_id) ?? null : null;
  const father = animal.father_id ? animalsById.get(animal.father_id) ?? null : null;

  const motherReady = !!mother;
  const fatherReady = !!father;

  return [
    {
      key: 'maternal_grandmother',
      label: 'Avó materna',
      shortLabel: 'Avó M',
      requiredSex: 'female',
      targetAnimalId: mother?.id ?? 0,
      field: 'mother_id',
      value: mother?.mother_id ?? null,
      disabled: !motherReady,
      disabledReason: motherReady ? undefined : 'Defina a mãe primeiro',
    },
    {
      key: 'maternal_grandfather',
      label: 'Avô materno',
      shortLabel: 'Avô M',
      requiredSex: 'male',
      targetAnimalId: mother?.id ?? 0,
      field: 'father_id',
      value: mother?.father_id ?? null,
      disabled: !motherReady,
      disabledReason: motherReady ? undefined : 'Defina a mãe primeiro',
    },
    {
      key: 'paternal_grandmother',
      label: 'Avó paterna',
      shortLabel: 'Avó P',
      requiredSex: 'female',
      targetAnimalId: father?.id ?? 0,
      field: 'mother_id',
      value: father?.mother_id ?? null,
      disabled: !fatherReady,
      disabledReason: fatherReady ? undefined : 'Defina o pai primeiro',
    },
    {
      key: 'paternal_grandfather',
      label: 'Avô paterno',
      shortLabel: 'Avô P',
      requiredSex: 'male',
      targetAnimalId: father?.id ?? 0,
      field: 'father_id',
      value: father?.father_id ?? null,
      disabled: !fatherReady,
      disabledReason: fatherReady ? undefined : 'Defina o pai primeiro',
    },
    {
      key: 'mother',
      label: 'Mãe',
      shortLabel: 'Mãe',
      requiredSex: 'female',
      targetAnimalId: animal.id,
      field: 'mother_id',
      value: animal.mother_id,
      disabled: false,
    },
    {
      key: 'father',
      label: 'Pai',
      shortLabel: 'Pai',
      requiredSex: 'male',
      targetAnimalId: animal.id,
      field: 'father_id',
      value: animal.father_id,
      disabled: false,
    },
  ];
}

export function calfCategoryCode(sex: 'male' | 'female') {
  return sex === 'female' ? 'bezerra' : 'bezerro';
}

export function animalToSummary(animal: Animal): AnimalSummary {
  return {
    id: animal.id,
    property_id: animal.property_id,
    property_name: '',
    lot_id: animal.lot_id,
    lot_name: null,
    internal_number: animal.internal_number,
    animal_name: animal.name,
    sex: animal.sex,
    breed_abbreviation: animal.breed_abbreviation,
    breed_name: animal.breed_name,
    category_name: animal.category_name,
    category_code: animal.category_code,
    birth_date: animal.birth_date,
    current_weight_kg: animal.current_weight_kg,
    status: animal.status,
    is_archive: animal.is_archive,
    mother_id: animal.mother_id,
    father_id: animal.father_id,
    cover_photo_url: animal.cover_photo_url,
    cover_media_id: animal.cover_media_id,
  };
}

export function animalDisplayLabel(animal: Pick<Animal, 'internal_number' | 'name'> | null | undefined) {
  if (!animal) return '';
  return animal.name?.trim()
    ? `${animal.internal_number} — ${animal.name.trim()}`
    : animal.internal_number;
}

export function collectDescendantIds(rootId: number, animalsById: Map<number, Animal>): Set<number> {
  const descendants = new Set<number>();
  const animals = [...animalsById.values()];
  let changed = true;

  while (changed) {
    changed = false;
    for (const animal of animals) {
      if (descendants.has(animal.id)) continue;
      const fromMother = animal.mother_id === rootId || (animal.mother_id != null && descendants.has(animal.mother_id));
      const fromFather = animal.father_id === rootId || (animal.father_id != null && descendants.has(animal.father_id));
      if (fromMother || fromFather) {
        descendants.add(animal.id);
        changed = true;
      }
    }
  }

  return descendants;
}

export function isInvalidPedigreePick(
  subjectId: number,
  candidateId: number,
  animalsById: Map<number, Animal>,
): string | null {
  if (subjectId === candidateId) return 'O animal não pode ser parente de si mesmo.';

  const descendants = collectDescendantIds(subjectId, animalsById);
  if (descendants.has(candidateId)) {
    return 'Essa escolha criaria um ciclo na árvore (animal descendente não pode virar ancestral).';
  }

  return null;
}
